import {
  faChevronRight,
  faEllipsisVertical,
  faFileMedical,
  faStar
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';

import AppointmentWithinAvailability from '@/components/AppointmentWithinAvailability';
import FeedbackCard from '@/components/FeedbackCard';
import { Color } from '@/constants/color';
import { useWebJoinLink } from '@/hooks/useWebJoinLink';
import { ChartingStatus } from '@/types/appointments';
import AppointmentAttendanceStatus from '@/ui/components/AppointmentAttendanceStatus';
import AuditLogLink from '@/ui/components/AuditLogLink';
import Avatar from '@/ui/components/Avatar';
import Badge from '@/ui/components/Badge';
import Button from '@/ui/components/Button';
import Dropdown from '@/ui/components/Dropdown';
import ScheduledBySourceBadge from '@/ui/components/ScheduledBySourceBadge';
import Tooltip from '@/ui/components/Tooltip';
import { CHARTING_STATUS_LABELS, resolveChartingStatus } from '@/utils/chartingStatus';
import formatPersonName from '@/utils/formatPersonName';

import type { Props } from './types';

const chartingBadgeColor: Record<ChartingStatus, Color> = {
  [ChartingStatus.SESSION_COMPLETE]: Color.GREEN,
  [ChartingStatus.SESSION_INCOMPLETE]: Color.RED,
  [ChartingStatus.CHART_PENDING]: Color.ORANGE
};

/**
 * NOTE: This component is business/domain-specific and does not belong in the
 * generic UI primitives library (ui/components). It should be moved to a more
 * appropriate domain-specific location in the codebase.
 */
const AppointmentAttendeeCard = ({
  attendanceId,
  appointmentWithinAvailability,
  person,
  internalStatus,
  cancellationReason,
  chartingCompleted,
  chartingStatus,
  appointment,
  groupEnrollment,
  rescheduledBySource,
  feedback,
  isFocused = false,
  showJoinLink = false,
  enableEncounters = false,
  onViewProfile,
  onViewEncounter
}: Props) => {
  const { joinLink, isLoading: loadingJoinLink, copiedJoinLink, fetchJoinLink } = useWebJoinLink();

  const name = formatPersonName({ person });
  const resolvedChartingStatus = resolveChartingStatus(chartingStatus, chartingCompleted);
  const avatarName = formatPersonName({ person, withPronouns: false });

  return (
    <li
      className={`relative gap-x-6 px-4 py-4 hover:bg-gray-50 dark:hover:bg-gray-700 sm:px-6${isFocused ? ' bg-blue-50 dark:bg-blue-950 hover:bg-blue-100 dark:hover:bg-blue-900' : ''}`}>
      {/* Absolute overlay makes the whole row navigate to the profile */}
      <div className="relative px-4 py-4">
        <button
          className="mp-sensitive absolute inset-0 z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
          onClick={() => onViewProfile(person.id)}
          type="button"
          aria-label={`View ${name}'s profile`}
        />

        {/* Name row: avatar+name on left, kebab+chevron (+ buttons at sm+) on right */}
        <div className="flex justify-between items-start gap-x-3">
          <div className="flex min-w-0 gap-x-3 items-center">
            {isFocused && (
              <FontAwesomeIcon icon={faStar} className="relative flex-none text-yellow-500" />
            )}
            <Avatar
              name={avatarName}
              avatarUrl={person.profile_image_url}
              className="relative flex-none"
            />
            <div className="min-w-0 flex-auto">
              <p className="text-sm font-semibold text-gray-900">{name}</p>
              <div className="mt-0.5 flex flex-wrap gap-1 items-center">
                {/* z-20 lifts the pill above the row's absolute view-profile overlay (z-10),
                    which otherwise intercepts hover and the tooltip never opens. */}
                <span className="relative z-20">
                  <AppointmentAttendanceStatus
                    status={internalStatus}
                    reason={cancellationReason}
                  />
                </span>
                {internalStatus === 'attended' && (
                  <Badge color={chartingBadgeColor[resolvedChartingStatus]}>
                    {CHARTING_STATUS_LABELS[resolvedChartingStatus]}
                  </Badge>
                )}
                <ScheduledBySourceBadge
                  scheduledBySource={appointment.scheduled_by_source}
                  initiator={appointment.initiator}
                  groupEnrollment={groupEnrollment}
                />{' '}
                {rescheduledBySource && (
                  <Tooltip tooltipContent="How this appointment was rescheduled">
                    <Badge color={Color.ORANGE} text={rescheduledBySource} />
                  </Tooltip>
                )}
                <AppointmentWithinAvailability
                  appointmentWithinAvailability={appointmentWithinAvailability}
                />
              </div>
            </div>
          </div>

          <div className="relative z-20 flex shrink-0 items-center gap-2">
            {/* Action buttons inline at sm+ only */}
            <div className="hidden sm:flex items-center gap-2">
              {enableEncounters && onViewEncounter && (
                <Button size="xs" color={Color.BLUE} onClick={() => onViewEncounter(attendanceId)}>
                  <FontAwesomeIcon icon={faFileMedical} className="mr-1" />
                  View Encounter
                </Button>
              )}
              {showJoinLink &&
                (!joinLink ? (
                  <Button
                    size="xs"
                    color={Color.CYAN}
                    onClick={() => fetchJoinLink(attendanceId)}
                    disabled={loadingJoinLink}
                    loading={loadingJoinLink}
                    loadingWithSpinner={false}>
                    Get Member Zoom Link
                  </Button>
                ) : (
                  <span className="relative flex items-center gap-1.5">
                    {copiedJoinLink && (
                      <span className="text-green-600 text-xs font-medium">✓ Copied</span>
                    )}
                    <a
                      className="text-blue-600 underline text-xs"
                      href={joinLink}
                      target="_blank"
                      rel="noreferrer">
                      Open
                    </a>
                  </span>
                ))}
            </div>
            <Dropdown
              buttonText={<FontAwesomeIcon icon={faEllipsisVertical} />}
              showChevronIcon={false}
              customButtonClassName="p-1 text-gray-400 hover:text-gray-600"
              items={[
                <AuditLogLink
                  key="audit"
                  entityType="appointmentattendance"
                  entityId={attendanceId}
                  className="px-3 py-2 text-sm text-gray-700">
                  Audit Log
                </AuditLogLink>
              ]}
            />
            <button
              type="button"
              onClick={() => onViewProfile(person.id)}
              className="mp-sensitive hidden sm:block"
              aria-label={`View ${name}'s profile`}>
              <FontAwesomeIcon icon={faChevronRight} className="flex-none text-gray-400" />
            </button>
          </div>
        </div>

        {/* Action buttons below name row on narrow only */}
        {(enableEncounters || showJoinLink) && (
          <div className="sm:hidden relative z-20 mt-2 flex flex-wrap gap-2">
            {enableEncounters && onViewEncounter && (
              <Button size="xs" color={Color.BLUE} onClick={() => onViewEncounter(attendanceId)}>
                <FontAwesomeIcon icon={faFileMedical} className="mr-1" />
                View Encounter
              </Button>
            )}
            {showJoinLink &&
              (!joinLink ? (
                <Button
                  size="xs"
                  color={Color.CYAN}
                  onClick={() => fetchJoinLink(attendanceId)}
                  disabled={loadingJoinLink}
                  loading={loadingJoinLink}
                  loadingWithSpinner={false}>
                  Get Member Zoom Link
                </Button>
              ) : (
                <span className="relative flex items-center gap-1.5">
                  {copiedJoinLink && (
                    <span className="text-green-600 text-xs font-medium">✓ Copied</span>
                  )}
                  <a
                    className="text-blue-600 underline text-xs"
                    href={joinLink}
                    target="_blank"
                    rel="noreferrer">
                    Open
                  </a>
                </span>
              ))}
          </div>
        )}
      </div>
      {feedback && feedback.length > 0 && (
        <div className="relative z-20 mt-2 pl-4">
          <FeedbackCard
            feedback={feedback[0]}
            personId={person.id}
            showTitle={false}
            showLink={true}
            showTimestamp={true}
            truncateText={true}
            minimal={true}
          />
        </div>
      )}
    </li>
  );
};

export default React.memo(AppointmentAttendeeCard);
